import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Star } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LanguageContext';
import { api } from '../lib/api';
import t from '../translations';
import ZodiacProfileCard from '../components/ZodiacProfileCard';

const SIGN_SYMBOLS = {
  Mesh:'♈', Vrishabh:'♉', Mithun:'♊', Kark:'♋',
  Simha:'♌', Kanya:'♍', Tula:'♎', Vrishchik:'♏',
  Dhanu:'♐', Makar:'♑', Kumbh:'♒', Meen:'♓',
};
const PLANET_META = {
  Sun:'☉', Moon:'☽', Mars:'♂', Mercury:'☿', Jupiter:'♃',
  Venus:'♀', Saturn:'♄', Rahu:'☊', Ketu:'☋',
};

function SectionCard({ title, children }) {
  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{
        padding: '12px 18px 10px',
        borderBottom: '1px solid rgba(201,168,76,0.1)',
        background: 'rgba(201,168,76,0.03)',
      }}>
        <p style={{ fontSize: 11, color: '#C9A84C', letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 700 }}>
          {title}
        </p>
      </div>
      <div style={{ padding: '14px 18px 16px' }}>{children}</div>
    </div>
  );
}

export default function MyChartPage() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { lang } = useLang();
  const tr = t[lang];

  const [details, setDetails] = useState(null);
  const [cosmicSnap, setCosmicSnap] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.allSettled([
      api.get('/api/user/chart-details'),
      api.get('/api/cosmic-snapshot'),
    ]).then(([detRes, cosRes]) => {
      if (detRes.status === 'fulfilled') setDetails(detRes.value);
      if (cosRes.status === 'fulfilled') setCosmicSnap(cosRes.value);
      setLoading(false);
    });
  }, []);

  if (!profile) return null;

  const chart = profile.chart;

  return (
    <div data-testid="my-chart-page" style={{ minHeight: '100vh', background: '#0A0F1E', paddingBottom: 80 }}>

      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '56px 20px 16px',
      }}>
        <button
          data-testid="back-btn"
          onClick={() => navigate('/home')}
          style={{ background: 'none', border: 'none', color: '#8A8580', cursor: 'pointer', padding: 4 }}
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <p style={{ fontSize: 11, color: '#C9A84C', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 2 }}>
            {lang === 'hi' ? 'ज्योतिष' : 'Jyotish'}
          </p>
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 700, color: '#F5F0E8' }}>
            {tr.myChart}
          </h1>
        </div>
        <Star size={18} color="#C9A84C" style={{ marginLeft: 'auto' }} />
      </div>

      <div style={{ padding: '0 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>

        {/* Full ZodiacProfileCard (expandable planets) */}
        <div className="fade-in-up">
          <ZodiacProfileCard profile={profile} />
        </div>

        {/* Birth Details */}
        {details && (
          <div className="fade-in-up-d1">
            <SectionCard title={tr.birthDetailsCard}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {[
                  { label: lang === 'hi' ? 'नाम' : 'Name',        value: profile.name },
                  { label: tr.dateOfBirth,  value: details.birthDate },
                  { label: tr.timeOfBirth,  value: details.birthTime || tr.noBirthTime },
                  { label: tr.placeOfBirth, value: details.birthCity },
                ].map(({ label, value }) => value && (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 12, color: '#8A8580' }}>{label}</span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#F5F0E8' }}>{value}</span>
                  </div>
                ))}
              </div>
            </SectionCard>
          </div>
        )}

        {/* Nakshatra */}
        {details?.nakshatra && (
          <div className="fade-in-up-d1">
            <SectionCard title={tr.birthNakshatra}>
              <div style={{ textAlign: 'center', marginBottom: 12 }}>
                <p style={{ fontSize: 26, fontFamily: "'Playfair Display', serif", fontWeight: 700, color: '#C9A84C' }}>
                  {details.nakshatra.nakshatra}
                </p>
                <p style={{ fontSize: 12, color: '#8A8580', marginTop: 4 }}>
                  {lang === 'hi' ? 'चंद्रमा में' : 'Moon in'} {details.nakshatra.moonSign}
                </p>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-around', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: 12 }}>
                <div style={{ textAlign: 'center' }}>
                  <p style={{ fontSize: 10, color: '#8A8580', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>{tr.nakshatraDeity}</p>
                  <p style={{ fontSize: 13, fontWeight: 600, color: '#F5F0E8' }}>{details.nakshatra.deity}</p>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <p style={{ fontSize: 10, color: '#8A8580', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>{tr.nakshatraLord}</p>
                  <p style={{ fontSize: 13, fontWeight: 600, color: '#F5F0E8' }}>{details.nakshatra.lord}</p>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <p style={{ fontSize: 10, color: '#8A8580', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>Pada</p>
                  <p style={{ fontSize: 13, fontWeight: 600, color: '#F5F0E8' }}>{details.nakshatra.pada}</p>
                </div>
              </div>
            </SectionCard>
          </div>
        )}

        {/* Dasha */}
        {details?.dasha && details.dasha.mahadasha !== 'Unknown' && (
          <div className="fade-in-up-d2">
            <SectionCard title={tr.dashaTitle}>
              <div style={{ display: 'flex', gap: 12 }}>
                {/* Mahadasha */}
                <div style={{
                  flex: 1, textAlign: 'center',
                  background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.15)',
                  borderRadius: 12, padding: '14px 10px',
                }}>
                  <p style={{ fontSize: 10, color: '#8A8580', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>{tr.mahadasha}</p>
                  <p style={{ fontSize: 20, fontFamily: "'Playfair Display', serif", fontWeight: 700, color: '#C9A84C' }}>
                    {details.dasha.mahadasha}
                  </p>
                  <p style={{ fontSize: 10, color: '#8A8580', marginTop: 6 }}>
                    {tr.dashaEnds} {details.dasha.mahadasha_ends?.split('T')[0]}
                  </p>
                </div>
                {/* Antardasha */}
                {details.dasha.antardasha && (
                  <div style={{
                    flex: 1, textAlign: 'center',
                    background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: 12, padding: '14px 10px',
                  }}>
                    <p style={{ fontSize: 10, color: '#8A8580', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>{tr.antardasha}</p>
                    <p style={{ fontSize: 20, fontFamily: "'Playfair Display', serif", fontWeight: 700, color: '#F5F0E8' }}>
                      {details.dasha.antardasha}
                    </p>
                    <p style={{ fontSize: 10, color: '#8A8580', marginTop: 6 }}>
                      {tr.dashaEnds} {details.dasha.antardasha_ends?.split('T')[0]}
                    </p>
                  </div>
                )}
              </div>
            </SectionCard>
          </div>
        )}

        {/* Cosmic Snapshot — sky right now */}
        {cosmicSnap && (
          <div className="fade-in-up-d2">
            <SectionCard title={tr.currentSky}>
              <p style={{ fontSize: 10, color: '#8A8580', marginBottom: 10 }}>{cosmicSnap.asOf}</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 4px' }}>
                {(cosmicSnap.planets || []).map((p, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.07)',
                    borderRadius: 8, padding: '5px 10px',
                  }}>
                    <span style={{ fontSize: 13, color: '#C9A84C' }}>{PLANET_META[p.name] || '●'}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#F5F0E8', whiteSpace: 'nowrap' }}>{p.name}</span>
                    <span style={{ fontSize: 10, color: '#C9A84C' }}>{p.sign?.symbol}</span>
                    <span style={{ fontSize: 10, color: '#8A8580', whiteSpace: 'nowrap' }}>{p.sign?.sa}</span>
                  </div>
                ))}
              </div>
            </SectionCard>
          </div>
        )}

        {loading && (
          <div className="card" style={{ textAlign: 'center', padding: 32 }}>
            <p style={{ color: '#8A8580', fontSize: 13 }}>{tr.loading}</p>
          </div>
        )}

      </div>
    </div>
  );
}
